import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  VStack,
  HStack,
  Button,
  Text,
  Heading,
  Badge,
  useColorModeValue,
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  useToast,
  Alert,
  AlertIcon,
  SimpleGrid,
  Card,
  CardBody,
  CardHeader,
  Image,
  IconButton,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  Input,
  InputGroup,
  InputLeftElement
} from '@chakra-ui/react';
import { 
  ChevronRightIcon, 
  AddIcon,
  EditIcon,
  DeleteIcon,
  CopyIcon
} from '@chakra-ui/icons';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { 
  FiGrid, 
  FiPlus, 
  FiEdit, 
  FiTrash2, 
  FiCopy, 
  FiMoreVertical,
  FiSearch,
  FiLayout
} from 'react-icons/fi';
import apiClient from '../../../utils/apiClient';

const LayoutLibrary = () => {
  const [layouts, setLayouts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');

  const toast = useToast();
  const navigate = useNavigate();

  const user = JSON.parse(sessionStorage.getItem('user') || '{}');
  const tenantId = user?.tenant_ids?.[0];

  const borderColor = useColorModeValue('gray.200', 'gray.600');
  const cardBg = useColorModeValue('white', 'gray.700');

  useEffect(() => {
    if (tenantId) {
      fetchLayouts();
    }
  }, [tenantId]);

  const fetchLayouts = async () => {
    try {
      setLoading(true);
      const response = await apiClient(`/component-layouts/${tenantId}`);
      
      let layoutsData = response.data || [];

    //   let layoutsData = [];
    //   if (response.success) {
    //     layoutsData = Object.keys(response)
    //       .filter(key => key !== 'success')
    //       .map(key => response[key]);
    //   } else if (Array.isArray(response)) {
    //     layoutsData = response;
    //   } else if (response.data) {
    //     layoutsData = Array.isArray(response.data) ? response.data : [];
    //   }
    //   console.log('layoutsData', layoutsData)
      setLayouts(layoutsData);

    } catch (error) {
      console.error('Error fetching layouts:', error);
      toast({
        title: 'Error loading layouts',
        description: error.message || 'Failed to load layouts',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
      setLayouts([]);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteLayout = async (layout) => {
    if (!window.confirm(`Are you sure you want to delete "${layout.name}"?`)) {
      return;
    }

    try {
      const response = await apiClient(`/component-layouts/${tenantId}/${layout.id}`, {
        method: 'DELETE'
      });

      if (response.error) {
        throw new Error(response.error);
      }

      toast({
        title: 'Layout deleted successfully',
        description: `"${layout.name}" has been deleted`,
        status: 'success',
        duration: 3000,
        isClosable: true,
      });

      fetchLayouts();
    } catch (error) {
      console.error('Error deleting layout:', error);
      toast({
        title: 'Error deleting layout',
        description: error.message || 'Failed to delete layout',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  };

  const filteredLayouts = layouts.filter(layout => {
    console.log('layout1', layout)


    if (!searchTerm) return true;
    const searchLower = searchTerm.toLowerCase();
    return (
      layout.name?.toLowerCase().includes(searchLower) ||
      layout.description?.toLowerCase().includes(searchLower) ||
      layout.category?.toLowerCase().includes(searchLower)
    );
  });

  return (
    <Container maxW="container.2xl" py={6}>
      <Breadcrumb mb={6} separator={<ChevronRightIcon color="gray.500" />}>
        <BreadcrumbItem>
          <BreadcrumbLink as={RouterLink} to="/">Dashboard</BreadcrumbLink>
        </BreadcrumbItem>
        <BreadcrumbItem>
          <BreadcrumbLink as={RouterLink} to="/pages">Pages</BreadcrumbLink>
        </BreadcrumbItem>
        <BreadcrumbItem isCurrentPage>
          <BreadcrumbLink>Layout Library</BreadcrumbLink>
        </BreadcrumbItem>
      </Breadcrumb>

      <VStack spacing={6} align="stretch">
        <HStack justify="space-between">
          <Heading size="lg">Layout Library</Heading>
          <Button
            leftIcon={<FiPlus />}
            colorScheme="blue"
            onClick={() => navigate('/layout-library/create')}
          >
            Create Layout
          </Button>
        </HStack>

        <HStack spacing={4}>
          <InputGroup maxW="400px">
            <InputLeftElement>
              <FiSearch />
            </InputLeftElement>
            <Input
              placeholder="Search layouts..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </InputGroup>
        </HStack>

        <SimpleGrid columns={{ base: 1, md: 2, lg: 3, xl: 4 }} spacing={6}>
          {console.log('filteredLayouts', filteredLayouts)}
          {filteredLayouts.map(layout => (
            console.log('layout2', layout),
            
            <LayoutCard
              key={layout.id}
              layout={layout}
              onEdit={() => navigate(`/layout-library/edit/${layout.id}`)}
              onDelete={() => handleDeleteLayout(layout)}
            />
          ))}
        </SimpleGrid>

        {filteredLayouts.length === 0 && !loading && (
          <Box textAlign="center" py={12}>
            <FiLayout size={48} color="gray.400" />
            <Heading size="md" mt={4} mb={2} color="gray.500">
              No layouts found
            </Heading>
            <Text color="gray.500" mb={6}>
              {searchTerm 
                ? "Try adjusting your search criteria"
                : "Get started by creating your first layout"
              }
            </Text>
            <Button
              leftIcon={<FiPlus />}
              colorScheme="blue"
              onClick={() => navigate('/layout-library/create')}
            >
              Create Layout
            </Button>
          </Box>
        )}
      </VStack>
    </Container>
  );
};

const LayoutCard = ({ layout, onEdit, onDelete }) => {
  const cardBg = useColorModeValue('white', 'gray.700');
  const borderColor = useColorModeValue('gray.200', 'gray.600');

  const getCategoryColor = (category) => {
    switch (category) {
      case 'header': return 'blue';
      case 'footer': return 'green';
      case 'section': return 'purple';
      case 'page': return 'orange';
      default: return 'gray';
    }
  };

  return (
    <Card
      bg={cardBg}
      borderColor={borderColor}
      borderWidth="1px"
      _hover={{ transform: 'translateY(-2px)', shadow: 'lg' }}
      transition="all 0.2s"
    >
      <CardHeader pb={2}>
        <HStack justify="space-between">
          <VStack align="start" spacing={1}>
            <Text fontWeight="semibold" fontSize="sm" noOfLines={1}>
              {layout.name}
            </Text>
            <Badge 
              size="xs" 
              colorScheme={getCategoryColor(layout.category)}
              variant="subtle"
            >
              {layout.category}
            </Badge>
          </VStack>
          
          <Menu>
            <MenuButton
              as={IconButton}
              icon={<FiMoreVertical />}
              variant="ghost"
              size="sm"
            />
            <MenuList>
              <MenuItem icon={<FiEdit />} onClick={onEdit}>
                Edit
              </MenuItem>
              {!layout.is_system && (
                <MenuItem icon={<FiTrash2 />} onClick={onDelete} color="red.500">
                  Delete
                </MenuItem>
              )}
            </MenuList>
          </Menu>
        </HStack>
      </CardHeader>

      <CardBody pt={0}>
        {layout.preview_image && (
          <Image
            src={layout.preview_image}
            alt={layout.name}
            borderRadius="md"
            mb={3}
            h="120px"
            w="100%"
            objectFit="cover"
            bg="gray.100"
          />
        )}
        
        <Text fontSize="xs" color="gray.600" noOfLines={3} mb={3}>
          {layout.description}
        </Text>

        <HStack justify="space-between" align="center">
          <HStack spacing={1}>
            {layout.is_system && (
              <Badge size="xs" colorScheme="purple">
                System
              </Badge>
            )}
            {layout.is_public && (
              <Badge size="xs" colorScheme="green">
                Public
              </Badge>
            )}
          </HStack>
          
          <Text fontSize="xs" color="gray.500">
            Used {layout.usage_count || 0} times
          </Text>
        </HStack>
      </CardBody>
    </Card>
  );
};

export default LayoutLibrary;